import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {
    ProjectRole,
    ProjectRoleId,
    ProjectRoleModule, ProjectRoleRequest,
    ProjectRoleRequestProp
} from "../../models/projects/ProjectRole";
import {  ofType, StateObservable } from "redux-observable";
import { switchMap, map, catchError } from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {Nullable} from "../../models/common/Nullable";
import {Identifier, makeNumericId} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";

/**
 * base API path to interact with project roles
 */
function apiPath( id: Nullable<number> = null ): string {
    return APP_CONFIG.baseWeb + "projects/roles/"+ ( id !== null ? id : '');
}

/** union of all project role actions types */
export type ProjectRoleActionTypes = FetchProjectRolesActionTypes
    | FetchProjectRoleByIdActionTypes
    | CreateProjectRoleActionTypes
    | UpdateProjectRoleActionTypes
    | DeleteProjectRoleActionTypes
    | RestoreProjectRoleActionTypes

/**
 * FETCH PROJECT ROLES BY USER VIEW SETTINGS
 *
 * user view settings will automatically be retrieved from redux store
 */
// TYPESCRIPT REDUX ACTION TYPES
export interface FetchProjectRolesAction extends ViewIdentifiable, ReduxAction< typeof FETCH_PROJECT_ROLES > {}
export interface FetchProjectRolesSuccessAction extends ViewIdentifiable, ReduxAction<typeof FETCH_PROJECT_ROLES_SUCCESS > {
    payload: ApiReturnType< ProjectRole[] >
}
export interface FetchProjectRolesErrorAction extends ViewIdentifiable, ErrorAction< typeof FETCH_PROJECT_ROLES_ERROR >{}

export type FetchProjectRolesActionTypes = FetchProjectRolesAction
    | FetchProjectRolesErrorAction
    | FetchProjectRolesSuccessAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_PROJECT_ROLES = "FETCH_PROJECT_ROLES";
export const FETCH_PROJECT_ROLES_SUCCESS = "FETCH_PROJECT_ROLES_SUCCESS";
export const FETCH_PROJECT_ROLES_ERROR = "FETCH_PROJECT_ROLES_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function fetchProjectRoles( viewIdentifier: string ) : FetchProjectRolesAction {
    return { type: FETCH_PROJECT_ROLES, viewIdentifier };
}
export function fetchProjectRolesSuccess(
    viewIdentifier: string,
    payload: ApiReturnType< ProjectRole[] >
) : FetchProjectRolesSuccessAction
{
    return { type: FETCH_PROJECT_ROLES_SUCCESS, viewIdentifier, payload };
}
export function fetchProjectRolesError( viewIdentifier: string, error: Error ): FetchProjectRolesErrorAction {
    return { type: FETCH_PROJECT_ROLES_ERROR, viewIdentifier, error };
}

// REDUX OBSERVABLE EPIC
export function fetchProjectRolesEpic(
    action$: Observable< FetchProjectRolesAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_ROLES ),
        switchMap( action => {
            const request = getUserViewSettingsApiRequest(
                apiPath(),
                action.viewIdentifier,
                store$
            );

            return ajax( APP_CONFIG.getRequestObject( request.path ) ).pipe(
                map( response => {
                ///    console.log( response )
                    return fetchProjectRolesSuccess(
                        action.viewIdentifier,
                        response.response as ApiReturnType< ProjectRole[] >
                    )
                } ),
                catchError(error => of(
                    fetchProjectRolesError( action.viewIdentifier, error),
                    eventNotificationError(error, store$, action.viewIdentifier, "fetchProjectRoles")
                ))
            );

        })
    );
}

/**
 * FETCH SINGLE PROJECT ROLE
 */
export interface FetchProjectRoleByIdAction extends ProjectRoleId, ReduxAction< typeof FETCH_PROJECT_ROLE_BY_ID> {

}
export interface FetchProjectRoleByIdSuccessAction extends ReduxAction< typeof FETCH_PROJECT_ROLE_BY_ID_SUCCESS> {
    role: ProjectRole
}
export interface FetchProjectRoleByIdErrorAction extends ProjectRoleId, ErrorAction< typeof FETCH_PROJECT_ROLE_BY_ID_ERROR> {

}
//union of action to fetch by id
export type FetchProjectRoleByIdActionTypes = FetchProjectRoleByIdAction
    | FetchProjectRoleByIdSuccessAction
    | FetchProjectRoleByIdErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_PROJECT_ROLE_BY_ID = "FETCH_PROJECT_ROLE_BY_ID";
export const FETCH_PROJECT_ROLE_BY_ID_SUCCESS = "FETCH_PROJECT_ROLE_BY_ID_SUCCESS";
export const FETCH_PROJECT_ROLE_BY_ID_ERROR = "FETCH_PROJECT_ROLE_BY_ID_ERROR";

//Redux action accessors function
export function fetchProjectRoleById( projectRoleId: Identifier<number> ): FetchProjectRoleByIdAction {
    return { type: FETCH_PROJECT_ROLE_BY_ID, projectRoleId };
}
export function fetchProjectRoleByIdSuccess( role: ProjectRole ): FetchProjectRoleByIdSuccessAction {
    return { type: FETCH_PROJECT_ROLE_BY_ID_SUCCESS, role };
}
export function fetchProjectRoleByIdError( error: any, projectRoleId: Identifier<number> ): FetchProjectRoleByIdErrorAction {
    return { type: FETCH_PROJECT_ROLE_BY_ID_ERROR, error, projectRoleId };
}

//redux observable epic as bridge to rxjs
export function fetchProjectRoleByIdEpic(
    action$: Observable< FetchProjectRoleByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_ROLE_BY_ID ),
        switchMap( action => ajax(
                APP_CONFIG.getRequestObject( apiPath( action.projectRoleId.rawValue ) )
            ).pipe(
                map( response => fetchProjectRoleByIdSuccess( response.response as ProjectRole ) ),
                catchError( error => of(
                    fetchProjectRoleByIdError( error, action.projectRoleId ),
                    eventNotificationError( error, store$, ProjectRoleModule.identifier, "fetchProjectRoleById" )
                ) )
            )
        )
    );
}

/**
 * MUTATING REQUESTS
 */

/**
 * ApiMutatingRoleRequest is the type which the role request must have to fit API backend class model
 */
interface ApiMutatingRoleRequest extends Omit< ProjectRoleRequest, 'qualifications' > {
    qualifications: number[]
}

/**
 * changes the project role request to the api backend required model
 *
 * @param projectRoleReq
 */
function roleRequestToApiRoleRequest(  projectRoleReq: ProjectRoleRequest ): ApiMutatingRoleRequest {
    return {
        ...projectRoleReq,
        qualifications: projectRoleReq.qualifications.map( _val => _val.id )
    };
}

/**
 * CREATE PROJECT ROLE
 */
export interface CreateProjectRoleAction extends ProjectRoleRequestProp, ReduxAction<typeof CREATE_PROJECT_ROLE> {

}
export interface CreateProjectRoleSuccessAction extends ProjectRoleId, ReduxAction<typeof CREATE_PROJECT_ROLE_SUCCESS> {

}
export interface CreateProjectRoleErrorAction extends ErrorAction<typeof CREATE_PROJECT_ROLE_ERROR> {

}
// typescript union type of create actions
export type CreateProjectRoleActionTypes = CreateProjectRoleAction
    | CreateProjectRoleSuccessAction
    | CreateProjectRoleErrorAction

//redux action constants
export const CREATE_PROJECT_ROLE = "CREATE_PROJECT_ROLE";
export const CREATE_PROJECT_ROLE_SUCCESS = "CREATE_PROJECT_ROLE_SUCCESS";
export const CREATE_PROJECT_ROLE_ERROR = "CREATE_PROJECT_ROLE_ERROR";

//redux action accessors functions
export function createProjectRole( projectRoleReq: ProjectRoleRequest ): CreateProjectRoleAction {
    return { type: CREATE_PROJECT_ROLE, projectRoleReq };
}
export function createProjectRoleSuccess( projectRoleId: Identifier<number> ): CreateProjectRoleSuccessAction {
    return { type: CREATE_PROJECT_ROLE_SUCCESS, projectRoleId };
}
export function createProjectRoleError( error: any ): CreateProjectRoleErrorAction {
    return { type: CREATE_PROJECT_ROLE_ERROR, error };
}

//redux observable rxjs bridge
export function createProjectRoleEpic(
    action$: Observable< CreateProjectRoleAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_PROJECT_ROLE ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.post,
                    roleRequestToApiRoleRequest( action.projectRoleReq )
                )
            ).pipe(
                switchMap( response => of(
                    createProjectRoleSuccess( ( response.response as ProjectRoleId ).projectRoleId ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.projectRoles.routes.main.path,
                        ProjectRoleModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createProjectRoleError( error ),
                    eventNotificationError( error, store$, ProjectRoleModule.identifier, "createProjectRole" )
                ))
            )
        )
    )
}

/**
 * UPDATE PROJECT ROLE
 */
export interface UpdateProjectRoleAction extends ProjectRoleRequestProp, ReduxAction<typeof UPDATE_PROJECT_ROLE> {

}
export interface UpdateProjectRoleSuccessAction extends ProjectRoleId, ReduxAction<typeof UPDATE_PROJECT_ROLE_SUCCESS> {

}
export interface UpdateProjectRoleErrorAction extends ErrorAction<typeof UPDATE_PROJECT_ROLE_ERROR> {

}
// typescript union type of update actions
export type UpdateProjectRoleActionTypes = UpdateProjectRoleAction
    | UpdateProjectRoleSuccessAction
    | UpdateProjectRoleErrorAction

//redux action constants
export const UPDATE_PROJECT_ROLE = "UPDATE_PROJECT_ROLE";
export const UPDATE_PROJECT_ROLE_SUCCESS = "UPDATE_PROJECT_ROLE_SUCCESS";
export const UPDATE_PROJECT_ROLE_ERROR = "UPDATE_PROJECT_ROLE_ERROR";

//redux action accessors functions
export function updateProjectRole( projectRoleReq: ProjectRoleRequest ): UpdateProjectRoleAction {
    return { type: UPDATE_PROJECT_ROLE, projectRoleReq };
}
export function updateProjectRoleSuccess( projectRoleId: Identifier<number> ): UpdateProjectRoleSuccessAction {
    return { type: UPDATE_PROJECT_ROLE_SUCCESS, projectRoleId };
}
export function updateProjectRoleError( error: any ): UpdateProjectRoleErrorAction {
    return { type: UPDATE_PROJECT_ROLE_ERROR, error };
}

//redux observable rxjs bridge
export function updateProjectRoleEpic(
    action$: Observable< UpdateProjectRoleAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_PROJECT_ROLE ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.projectRoleReq.id ),
                    RouteMethods.put,
                    roleRequestToApiRoleRequest( action.projectRoleReq )
                )
            ).pipe(
                switchMap( _ => of(
                    updateProjectRoleSuccess( makeNumericId( action.projectRoleReq.id )),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.projectRoles.routes.main.path,
                        ProjectRoleModule.identifier
                    )
                ) ),
                catchError( error => of(
                    updateProjectRoleError( error ),
                    eventNotificationError( error, store$, ProjectRoleModule.identifier, "updateProjectRole" )
                ))
            )
        )
    )
}

/**
 * DELETE PROJECT ROLE
 */
export interface DeleteProjectRoleAction extends ProjectRoleId, ReduxAction<typeof DELETE_PROJECT_ROLE> {

}
export interface DeleteProjectRoleSuccessAction extends ProjectRoleId, ReduxAction<typeof DELETE_PROJECT_ROLE_SUCCESS> {

}
export interface DeleteProjectRoleErrorAction extends ErrorAction<typeof DELETE_PROJECT_ROLE_ERROR> {

}
// typescript union type of delete actions
export type DeleteProjectRoleActionTypes = DeleteProjectRoleAction
    | DeleteProjectRoleSuccessAction
    | DeleteProjectRoleErrorAction

//redux action constants
export const DELETE_PROJECT_ROLE = "DELETE_PROJECT_ROLE";
export const DELETE_PROJECT_ROLE_SUCCESS = "DELETE_PROJECT_ROLE_SUCCESS";
export const DELETE_PROJECT_ROLE_ERROR = "DELETE_PROJECT_ROLE_ERROR";

//redux action accessors functions
export function deleteProjectRole( projectRoleId: Identifier<number>  ): DeleteProjectRoleAction {
    return { type: DELETE_PROJECT_ROLE, projectRoleId };
}
export function deleteProjectRoleSuccess( projectRoleId: Identifier<number> ): DeleteProjectRoleSuccessAction {
    return { type: DELETE_PROJECT_ROLE_SUCCESS, projectRoleId };
}
export function deleteProjectRoleError( error: any ): DeleteProjectRoleErrorAction {
    return { type: DELETE_PROJECT_ROLE_ERROR, error };
}

//redux observable rxjs bridge
export function deleteProjectRoleEpic(
    action$: Observable< DeleteProjectRoleAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_PROJECT_ROLE ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.projectRoleId.rawValue ),
                    RouteMethods.delete
                )
            ).pipe(
                switchMap( _ => of(
                    deleteProjectRoleSuccess( action.projectRoleId ),
                    eventNotificationSuccess(),
                    fetchProjectRoleById( action.projectRoleId )
                ) ),
                catchError( error => of(
                    deleteProjectRoleError( error ),
                    eventNotificationError( error, store$, ProjectRoleModule.identifier, "deleteProjectRole" )
                ))
            )
        )
    )
}

/**
 * RESTORE PROJECT ROLE
 */
export interface RestoreProjectRoleAction extends ProjectRoleId, ReduxAction<typeof RESTORE_PROJECT_ROLE> {

}
export interface RestoreProjectRoleSuccessAction extends ProjectRoleId, ReduxAction<typeof RESTORE_PROJECT_ROLE_SUCCESS> {

}
export interface RestoreProjectRoleErrorAction extends ErrorAction<typeof RESTORE_PROJECT_ROLE_ERROR> {

}
// typescript union type of restore actions
export type RestoreProjectRoleActionTypes = RestoreProjectRoleAction
    | RestoreProjectRoleSuccessAction
    | RestoreProjectRoleErrorAction

//redux action constants
export const RESTORE_PROJECT_ROLE = "RESTORE_PROJECT_ROLE";
export const RESTORE_PROJECT_ROLE_SUCCESS = "RESTORE_PROJECT_ROLE_SUCCESS";
export const RESTORE_PROJECT_ROLE_ERROR = "RESTORE_PROJECT_ROLE_ERROR";

//redux action accessors functions
export function restoreProjectRole( projectRoleId: Identifier<number>  ): RestoreProjectRoleAction {
    return { type: RESTORE_PROJECT_ROLE, projectRoleId };
}
export function restoreProjectRoleSuccess( projectRoleId: Identifier<number> ): RestoreProjectRoleSuccessAction {
    return { type: RESTORE_PROJECT_ROLE_SUCCESS, projectRoleId };
}
export function restoreProjectRoleError( error: any ): RestoreProjectRoleErrorAction {
    return { type: RESTORE_PROJECT_ROLE_ERROR, error };
}

//redux observable rxjs bridge
export function restoreProjectRoleEpic(
    action$: Observable< RestoreProjectRoleAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_PROJECT_ROLE ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.projectRoleId.rawValue ),
                    RouteMethods.patch
                )
            ).pipe(
                switchMap( _ => of(
                    restoreProjectRoleSuccess( action.projectRoleId ),
                    eventNotificationSuccess(),
                    fetchProjectRoleById( action.projectRoleId )
                ) ),
                catchError( error => of(
                    restoreProjectRoleError( error ),
                    eventNotificationError( error, store$, ProjectRoleModule.identifier, "restoreProjectRole" )
                ))
            )
        )
    )
}